import { useState } from 'react';
import { Calendar, CalendarDays } from 'lucide-react';

const TimeRangeFilter = ({ timeRange, setTimeRange, matches }) => {
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Calculate available date ranges from match data
  const getDateRanges = () => {
    const ranges = [
      { label: 'All Time', value: 'all' },
      { label: 'Last 7 Days', value: '7d' },
      { label: 'Last 30 Days', value: '30d' },
      { label: 'Last 90 Days', value: '90d' },
      { label: 'Last 6 Months', value: '6m' },
      { label: 'This Year', value: 'year' },
      { label: 'Custom Range...', value: 'custom' },
    ];

    return ranges;
  };

  const handleTimeRangeChange = value => {
    if (value === 'custom') {
      setShowCustomPicker(true);
    } else {
      setShowCustomPicker(false);
      setTimeRange(value);
    }
  };

  const applyCustomRange = () => {
    if (customStartDate && customEndDate) {
      setTimeRange(`custom:${customStartDate}:${customEndDate}`);
      setShowCustomPicker(false);
    }
  };

  const getDateRange = () => {
    if (!matches || matches.length === 0) return { minDate: '', maxDate: '' };

    const dates = matches.map(m => new Date(m.timestamp || m.date));
    const minDate = new Date(Math.min(...dates)).toISOString().split('T')[0];
    const maxDate = new Date(Math.max(...dates)).toISOString().split('T')[0];

    return { minDate, maxDate };
  };

  const { minDate, maxDate } = getDateRange();

  const dateRanges = getDateRanges();

  return (
    <div className="flex items-center gap-3">
      <Calendar className="w-4 h-4 text-zinc-400" />
      <div className="relative">
        <select
          value={timeRange.startsWith('custom:') ? 'custom' : timeRange}
          onChange={e => handleTimeRangeChange(e.target.value)}
          className="text-sm border border-zinc-200 rounded px-3 py-1.5 bg-white text-zinc-700 focus:outline-none focus:ring-2 focus:ring-zinc-400 focus:border-transparent"
        >
          {dateRanges.map(range => (
            <option key={range.value} value={range.value}>
              {range.label}
            </option>
          ))}
        </select>

        {showCustomPicker && (
          <div className="absolute top-full left-0 mt-2 p-4 bg-white border border-zinc-200 rounded-lg shadow-lg z-50 min-w-[300px]">
            <div className="flex items-center gap-2 mb-3">
              <CalendarDays className="w-4 h-4 text-zinc-500" />
              <span className="text-sm font-medium text-zinc-700">
                Select Date Range
              </span>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={customStartDate}
                    min={minDate}
                    max={maxDate}
                    onChange={e => setCustomStartDate(e.target.value)}
                    className="w-full text-sm border border-zinc-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-zinc-400"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={customEndDate}
                    min={customStartDate || minDate}
                    max={maxDate}
                    onChange={e => setCustomEndDate(e.target.value)}
                    className="w-full text-sm border border-zinc-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-zinc-400"
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  onClick={applyCustomRange}
                  disabled={!customStartDate || !customEndDate}
                  className="px-3 py-1.5 text-xs font-medium bg-zinc-900 text-white rounded hover:bg-zinc-800 disabled:bg-zinc-300 disabled:cursor-not-allowed"
                >
                  Apply Range
                </button>
                <button
                  onClick={() => setShowCustomPicker(false)}
                  className="px-3 py-1.5 text-xs font-medium border border-zinc-200 text-zinc-600 rounded hover:bg-zinc-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeRangeFilter;
